import { partnerComesToYou } from './partnerComesToYou';
import type { PartnerOption } from '../data/getGroupPartners';

/** One partner's computed round-trip transport cost, used to pick a cheaper alternative. */
export type TransportCandidate = {
    /** Whatever `partnerComesToYou` needs to decide, plus the id to report back. */
    partner: Pick<PartnerOption, 'activityId'> & Parameters<typeof partnerComesToYou>[0];
    /** Round-trip transport cost in EUR for this partner to the customer. */
    transportEUR: number;
};

export type CheaperAlternative = {
    activityId: number;
    /** EUR saved versus the currently-selected partner's transport. */
    savings: number;
};

/**
 * Pick the cheapest-transport alternative to the selected partner in custom mode
 * ("Aanbieder komt naar mij").
 *
 * Regression guard: the candidate set MUST be restricted to partners that actually travel to
 * the customer, via `partnerComesToYou` — the flags, not the presence of a tariff. Having no
 * tariff means travelling for free, not staying home.
 *
 * The alternative is only returned when it beats the selected partner's transport
 * cost by at least `threshold` (a fraction, e.g. 0.15 = 15%).
 */
export function selectCheaperAlternative(args: {
    candidates: ReadonlyArray<TransportCandidate>;
    selectedActivityId: number;
    selectedTransportEUR: number;
    threshold: number;
}): CheaperAlternative | null {
    const { candidates, selectedActivityId, selectedTransportEUR, threshold } = args;

    let best: TransportCandidate | null = null;
    for (const candidate of candidates) {
        if (candidate.partner.activityId === selectedActivityId) continue;
        // Never recommend a partner that does not travel to the customer.
        if (!partnerComesToYou(candidate.partner)) continue;
        if (!best || candidate.transportEUR < best.transportEUR) best = candidate;
    }
    if (!best) return null;

    const savings = selectedTransportEUR - best.transportEUR;
    // savings > 0 guarantees the selected transport cost is > 0, so the ratio is safe.
    if (savings <= 0) return null;
    if (savings / selectedTransportEUR < threshold) return null;

    return { activityId: best.partner.activityId, savings };
}
